import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all translation files
import en from '@/locales/en/translation.json';
import de from '@/locales/de/translation.json';
import fr from '@/locales/fr/translation.json';
import es from '@/locales/es/translation.json';
import it from '@/locales/it/translation.json';
import pl from '@/locales/pl/translation.json';
import ar from '@/locales/ar/translation.json';
import cs from '@/locales/cs/translation.json';
import da from '@/locales/da/translation.json';
import et from '@/locales/et/translation.json';
import fi from '@/locales/fi/translation.json';
import fil from '@/locales/fil/translation.json';
import hi from '@/locales/hi/translation.json';
import hu from '@/locales/hu/translation.json';
import hy from '@/locales/hy/translation.json';
import id from '@/locales/id/translation.json';
import ja from '@/locales/ja/translation.json';
import ko from '@/locales/ko/translation.json';
import lt from '@/locales/lt/translation.json';
import lv from '@/locales/lv/translation.json';
import ms from '@/locales/ms/translation.json';
import nb from '@/locales/nb/translation.json';
import nl from '@/locales/nl/translation.json';
import ptBR from '@/locales/pt-BR/translation.json';
import ptPT from '@/locales/pt-PT/translation.json';
import ro from '@/locales/ro/translation.json';
import ru from '@/locales/ru/translation.json';
import sk from '@/locales/sk/translation.json';
import sv from '@/locales/sv/translation.json';
import th from '@/locales/th/translation.json';
import tr from '@/locales/tr/translation.json';
import uk from '@/locales/uk/translation.json';
import vi from '@/locales/vi/translation.json';
import zh from '@/locales/zh/translation.json';

export const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'et', name: 'Eesti', flag: '🇪🇪' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'fil', name: 'Filipino', flag: '🇵🇭' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'hy', name: 'Հայերեն', flag: '🇦🇲' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'lt', name: 'Lietuvių', flag: '🇱🇹' },
  { code: 'lv', name: 'Latviešu', flag: '🇱🇻' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'nb', name: 'Norsk Bokmål', flag: '🇳🇴' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'pt-PT', name: 'Português (Portugal)', flag: '🇵🇹' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
] as const;

export type LanguageCode = (typeof languages)[number]['code'];

const resources = {
  en: { translation: en },
  de: { translation: de },
  fr: { translation: fr },
  es: { translation: es },
  it: { translation: it },
  pl: { translation: pl },
  ar: { translation: ar },
  cs: { translation: cs },
  da: { translation: da },
  et: { translation: et },
  fi: { translation: fi },
  fil: { translation: fil },
  hi: { translation: hi },
  hu: { translation: hu },
  hy: { translation: hy },
  id: { translation: id },
  ja: { translation: ja },
  ko: { translation: ko },
  lt: { translation: lt },
  lv: { translation: lv },
  ms: { translation: ms },
  nb: { translation: nb },
  nl: { translation: nl },
  'pt-BR': { translation: ptBR },
  'pt-PT': { translation: ptPT },
  ro: { translation: ro },
  ru: { translation: ru },
  sk: { translation: sk },
  sv: { translation: sv },
  th: { translation: th },
  tr: { translation: tr },
  uk: { translation: uk },
  vi: { translation: vi },
  zh: { translation: zh },
};

// Initialize i18n synchronously (no SSR with Vite)
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: languages.map((l) => l.code),
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'mk-contact-lang',
    },
  });

export default i18n;
